/**
 * stripe-checkout.js — OmyBoat — paiement réservation via Stripe Checkout (Stripe-only).
 *
 * Le bouton « Réservez maintenant » du widget BTA (iframe) poste normalement sur
 * POST /apps/bookthatapp/api/v1/reservations (booking BTA + checkout Shopify Payments).
 * Ici on intercepte CE post et on redirige vers Stripe Checkout
 * (POST <backend>/api/checkout/create → {url}). La commande Shopify est recréée par
 * l'API après paiement (webhook checkout.session.completed) → BTA ré-ingère le booking.
 *
 * ⚠️ STRIPE-ONLY, AUCUN FALLBACK (décision 2026-06-25) : une SOUMISSION de réservation
 * (POST /reservations AVEC items[0][externalId] + dates) n'est JAMAIS laissée à BTA. Si le
 * checkout Stripe ne peut pas démarrer (backend KO, dates prises, soumission illisible malgré
 * des items présents) → redirection vers la page d'erreur dédiée /pages/reservation-erreur.
 * On ne retombe jamais sur l'ancien flux Shopify Payments.
 * EXCEPTION (2026-07-13, cf. changelog ci-dessous) : un POST /reservations SANS items n'est pas
 * une soumission — c'est un appel auxiliaire BTA (retry/check_waitlist/check_expires) — il est
 * laissé passer vers BTA (isNonReservationPost), pas envoyé sur la page d'erreur.
 *
 * Plus de flag backend : on arme toujours (le flag STRIPE_CHECKOUT_ENABLED est supprimé).
 *
 * 2026-07-05 — télémétrie + durcissement (issue Sentry THEME-STOREFRONT-4, 1 event
 * Chrome Mobile/Android, cause indéterminable faute de métadonnées) :
 *   - parseReservation accepte les bodies non-string (URLSearchParams, FormData —
 *     détection par constructor.name : `instanceof` du parent ne matche pas les
 *     objets créés dans le realm de l'iframe BTA) ;
 *   - toFr tolère un horaire après la date (FR ou ISO) ;
 *   - POST /reservations sans body signalé (trou potentiel du Stripe-only) ;
 *   - widget BTA introuvable après 60s (onglet visible) signalé.
 *
 * 2026-07-08 — diag scrub-proof + parse multi-format (issue Sentry df3872f3, widget mobile
 * `bta-reservation-widget-xcomponent` suspecté). Le diag `keys` (chaîne jointe contenant
 * "cart_token") du 2026-07-05 a été scrubbé par Sentry en tant que sous-chaîne "token"
 * ("[Filtered]") → aucune métadonnée exploitable. Remplacé par des booléens par clé
 * attendue + noms de clés inattendues sanitisés (jamais de nom de clé brut en extra) :
 *   - parseReservation gère aussi un body JSON (hypothèse mobile) en plus des formats
 *     form-urlencodé/URLSearchParams/FormData existants ;
 *   - finish absent + start présent → fallback finish = start (mode "Journée") ;
 *   - interception /reservations resserrée : POST uniquement, chemin ancré (exclut les
 *     sous-routes /reservations/xxx et les GET, passthrough garanti par construction) ;
 *   - diag enrichi : widgetHint (search/reservation), bundleVersion, isMobileUA,
 *     jsonLike/jsonKeyPaths, bodyLength — en plus de bodyCtor/convertible (2026-07-05).
 *
 * 2026-07-09 — fix régression "Sentry muet" (repro live confirmée : erreur affichée
 * immédiatement en arrivant sur la fiche bateau, Chrome Android, AUCUNE alerte Sentry
 * reçue malgré reportError() appelé). Cause : sur les 2 chemins qui enchaînaient
 * reportError() → navigation immédiate (parseReservation illisible, échec startStripe),
 * window.top.location.href coupe la requête réseau Sentry avant son envoi (course
 * capture/navigation — captureException() ne garantit pas l'envoi synchrone).
 * 1re version du fix (tag "flush") : reportErrorThenRedirect() attendait Sentry.flush()
 * avant de naviguer — RÉVÉLÉ NO-OP EN PROD le jour même : le script Sentry chargé ici est
 * le "Loader Script" CDN (proxy léger : onLoad/init/captureException/… ), qui n'expose
 * JAMAIS flush() (vérifié en direct sur omyboat.fr, y compris 3s après captureException).
 * Sans flush, l'ancien code retombait sur `proceed()` immédiat → même course qu'avant le fix.
 * Version corrigée (même jour) : Sentry.forceLoad() appelé au chargement du script pour
 * télécharger le SDK complet par anticipation (réduit la fenêtre de course), et
 * reportErrorThenRedirect() utilise flush() SI disponible (SDK complet déjà chargé),
 * sinon un délai fixe de 900ms avant de naviguer (pas de garantie absolue — le SDK loader
 * n'utilise pas sendBeacon/keepalive — mais très supérieur à 0ms).
 * Les 2 chemins reportError() sans navigation (POST sans body, widget introuvable après
 * 60s) sont inchangés — pas de course puisque rien ne navigue derrière.
 *
 * 2026-07-13 — fix faux positifs bloquant le paiement (issues Sentry THEME-STOREFRONT-4/5/7,
 * ~40 events, en hausse). Le diag scrub-proof (2026-07-08) a enfin donné la cause réelle :
 * bodyLength=138, convertible, hasExternalId=FALSE, extraKeys="retry,check_waitlist,check_expires".
 * Le body n'était PAS illisible — ce n'était tout simplement PAS une réservation : BTA poste sur
 * le MÊME endpoint /reservations, à l'init du widget (createReservation(items, checkout, retry,
 * checkExpires) via componentDidMount→updatePropsItems), un appel auxiliaire sans items. L'ancien
 * code, ne trouvant pas d'items, éjectait l'utilisateur sur /pages/reservation-erreur (faux
 * positif → réservation bloquée, surtout mobile avec un cart_token existant). Les 3 patches
 * précédents (encodages exotiques) ne pouvaient pas régler ça. Fix : isNonReservationPost() →
 * passthrough vers BTA pour tout POST /reservations sans items[0][externalId], + télémétrie info
 * (reportInfo, niveau info donc pas d'alerte) pour garder la visibilité. Une vraie soumission
 * illisible (items présents mais dates KO) déclenche toujours l'alerte error + page d'erreur.
 * Couvert par cross-project-tests/stripe-checkout-passthrough.test.ts (jsdom, red→green).
 *
 * Réutilise le pattern éprouvé de cart-guard-iframe.js (découverte iframe + wrap fetch),
 * fichier depuis supprimé le 2026-07-13 — le pattern est autonome ici, aucune dépendance.
 * CLAUDE.md tripwire #4 : jamais inliné — <script src=… defer>.
 * Intégrité : validé par scripts/check-assets-js.mjs (pre-commit + CI) — ne jamais
 * committer/déployer une version qui ne passe pas `node --check` (incident troncature 2026-06-24).
 */
(function () {
  'use strict';

  var cfg = document.getElementById('stripe-checkout-config');
  if (!cfg) return;
  var BACKEND = (cfg.dataset.backendUrl || '').replace(/\/+$/, '');
  var PRODUCT_ID = parseInt(cfg.dataset.productId || '0', 10);
  var BOAT_TITLE = cfg.dataset.boatTitle || '';
  var LOCALE = (cfg.dataset.locale || 'fr').toLowerCase().indexOf('en') === 0 ? 'en' : 'fr';
  if (!BACKEND || !PRODUCT_ID) return;

  // Déclenche le téléchargement du SDK Sentry complet dès maintenant plutôt qu'à la 1re
  // erreur (Loader Script par défaut) — réduit la fenêtre de course décrite ci-dessous
  // dans reportErrorThenRedirect (constat prod 2026-07-09, Sentry.flush indisponible).
  try { if (window.Sentry && typeof window.Sentry.forceLoad === 'function') window.Sentry.forceLoad(); } catch (e) {}

  // N'intercepte que POST .../bookthatapp/api/v1/reservations (ancré fin de chemin, query ignorée).
  // Exclut les sous-routes (/reservations/xxx) et tout GET → passthrough par construction.
  var RESERVATIONS_PATH_RE = /\/bookthatapp\/api\/v1\/reservations$/;

  // ── Helpers dates : normalise vers "JJ/MM/AAAA" (format attendu par le backend) ─
  function toFr(v) {
    if (!v) return null;
    var s = String(v).trim();
    var iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);          // "AAAA-MM-JJ", avec ou sans heure
    if (iso) return iso[3] + '/' + iso[2] + '/' + iso[1];
    var fr = /^(\d{2})\/(\d{2})\/(\d{4})/.exec(s);         // "JJ/MM/AAAA", avec ou sans heure
    if (fr) return fr[1] + '/' + fr[2] + '/' + fr[3];
    return null;
  }

  // Réservation "Journée" : finish absent/vide → même jour que start.
  function applyFinishFallback(r) {
    if (r && r.start && !r.finish) r.finish = r.start;
    return r;
  }

  // Sérialise le body en string form-urlencoded quel que soit son type d'origine.
  // ⚠️ cross-realm : le body est créé DANS l'iframe BTA → `instanceof URLSearchParams`
  // évalué ici (parent) ne matche pas. Détection par constructor.name + duck-typing.
  function bodyToString(body) {
    if (typeof body === 'string') return body;
    if (!body || typeof body !== 'object') return null;
    var ctor = (body.constructor && body.constructor.name) || '';
    try {
      if (ctor === 'URLSearchParams') return String(body);
      if (ctor === 'FormData' && typeof body.forEach === 'function') {
        var pairs = [];
        body.forEach(function (v, k) {
          if (typeof v === 'string') pairs.push(encodeURIComponent(k) + '=' + encodeURIComponent(v));
        });
        return pairs.join('&');
      }
    } catch (e) { /* body exotique (stream, Blob…) → null */ }
    return null;
  }

  // Extraction tolérante depuis un objet JSON parsé : items[0] / reservation.items[0] / racine,
  // externalId ou external_id, finish ou end.
  function extractFromObj(obj) {
    if (!obj || typeof obj !== 'object') return null;
    var item = null;
    if (obj.items && obj.items[0]) item = obj.items[0];
    else if (obj.reservation && obj.reservation.items && obj.reservation.items[0]) item = obj.reservation.items[0];
    else item = obj;
    if (!item || typeof item !== 'object') return null;
    var id = item.externalId || item.external_id;
    if (!id) return null;
    var finish = item.finish || item.end;
    return { variantId: parseInt(id, 10), start: toFr(item.start), finish: toFr(finish) };
  }

  // Extrait { variantId, start, finish } du body /reservations. Multi-format :
  // - body JSON (string commençant par "{") → extraction tolérante via extractFromObj
  //   (hypothèse widget mobile) ;
  // - sinon form-urlencodé/URLSearchParams/FormData (format desktop confirmé) via bodyToString.
  function parseReservation(body) {
    if (typeof body === 'string') {
      var trimmed = body.trim();
      if (trimmed.charAt(0) === '{') {
        try {
          var r = extractFromObj(JSON.parse(trimmed));
          return r && r.variantId ? applyFinishFallback(r) : null;
        } catch (e) { return null; }
      }
    }
    var s = bodyToString(body);
    if (!s) return null;
    try {
      var p = new URLSearchParams(s);
      var id = p.get('items[0][externalId]');
      if (!id) return null;
      return applyFinishFallback({
        variantId: parseInt(id, 10),
        start: toFr(p.get('items[0][start]')),
        finish: toFr(p.get('items[0][finish]')),
      });
    } catch (e) { return null; }
  }

  // Un POST /reservations SANS items[0][externalId] n'est PAS une soumission de réservation :
  // c'est un appel auxiliaire de BTA (retry / check_waitlist / check_expires) émis à l'init du
  // widget sur l'endpoint PARTAGÉ. L'ancien code l'éjectait sur la page d'erreur → faux positif
  // qui bloquait des réservations mobiles (Sentry THEME-STOREFRONT-4/5/7, signature confirmée :
  // form-urlencodé, hasExternalId=false, extraKeys="retry,check_waitlist,check_expires").
  // On préfère le laisser passer vers BTA. Un body JSON ou non-convertible reste traité comme
  // 'illisible' (on ne relâche pas la garde Stripe-only sur un format qu'on ne sait pas lire).
  function isNonReservationPost(body) {
    var s = (typeof body === 'string') ? body : bodyToString(body);
    if (typeof s !== 'string') return false;
    var t = s.trim();
    if (t.charAt(0) === '{') return false; // JSON → pas ce cas connu, on garde le comportement erreur
    try { return !new URLSearchParams(t).has('items[0][externalId]'); }
    catch (e) { return false; }
  }

  // ── Diagnostic scrub-proof (aucune valeur, aucun nom de clé brut) ──────────────
  function sanitizeKeyName(k) {
    return String(k).replace(/token/gi, 't*ken').replace(/\d+/g, '#').slice(0, 60);
  }

  function collectKeyPaths(obj, prefix, depth) {
    var paths = [];
    if (depth <= 0 || !obj || typeof obj !== 'object') return paths;
    var keys = Array.isArray(obj) ? obj.map(function (_, i) { return i; }) : Object.keys(obj);
    for (var i = 0; i < keys.length; i++) {
      var k = keys[i];
      var full = prefix ? prefix + '.' + k : String(k);
      paths.push(full);
      paths = paths.concat(collectKeyPaths(obj[k], full, depth - 1));
    }
    return paths;
  }

  // Nom du bootstrap BTA chargé (corrélation avec les mises à jour BTA côté Sentry).
  function detectBundleVersion() {
    try {
      var scripts = document.querySelectorAll('script[src*="bookthatapp"]');
      if (scripts.length) {
        var src = scripts[scripts.length - 1].getAttribute('src') || '';
        var m = /\/([^\/?]+\.js)(?:\?|$)/.exec(src);
        return m ? m[1] : src.slice(0, 80);
      }
    } catch (e) {}
    return 'unknown';
  }

  // Diagnostic SANS PII quand parseReservation échoue : types, présence des champs (booléens
  // uniquement — jamais un nom de clé ou une valeur brute susceptible d'être scrubbé/lu comme
  // PII), forme structurelle du body — JAMAIS le body brut ni les valeurs (coordonnées locataire).
  function buildDiag(body, path, hint) {
    var d = {
      url: path,
      widgetHint: hint || 'unknown',
      bundleVersion: detectBundleVersion(),
      isMobileUA: /Mobi|Android/i.test(navigator.userAgent || ''),
      typeofBody: typeof body,
      bodyCtor: body === null ? 'null' : ((body && body.constructor && body.constructor.name) || typeof body),
    };
    var s = bodyToString(body);
    d.convertible = typeof s === 'string';
    d.bodyLength = d.convertible ? s.length : 0;
    var trimmed = d.convertible ? s.trim() : '';
    d.jsonLike = typeof body === 'string' && trimmed.charAt(0) === '{';
    d.hasCartToken = false; d.hasExternalId = false; d.hasStart = false; d.hasFinish = false; d.hasShop = false;
    d.extraKeys = ''; d.jsonKeyPaths = '';
    if (d.jsonLike) {
      try {
        d.jsonKeyPaths = collectKeyPaths(JSON.parse(trimmed), '', 3).slice(0, 40).join(',').slice(0, 200);
      } catch (e) { d.jsonKeyPaths = 'parse-error'; }
    } else if (d.convertible) {
      try {
        var p = new URLSearchParams(s);
        d.hasCartToken = p.has('cart_token');
        d.hasExternalId = p.has('items[0][externalId]');
        d.hasStart = p.has('items[0][start]');
        d.hasFinish = p.has('items[0][finish]');
        d.hasShop = p.has('shop');
        var expected = {
          'cart_token': 1, 'items[0][externalId]': 1, 'items[0][start]': 1, 'items[0][finish]': 1,
          'items[0][quantity]': 1, 'items[0][duration]': 1, 'items[0][location_name]': 1, 'shop': 1,
        };
        var extra = [];
        p.forEach(function (v, k) { if (!expected[k]) extra.push(sanitizeKeyName(k)); });
        d.extraKeys = extra.join(',').slice(0, 200);
      } catch (e) { d.extraKeys = 'parse-error'; }
    }
    return d;
  }

  // Sentry (projet theme-storefront) — best-effort : le loader script peut être bloqué par un
  // ad-blocker, donc jamais throw si window.Sentry est absent. Ne jamais logguer le body brut de
  // la requête BTA (peut contenir les coordonnées du locataire) — seulement des métadonnées.
  function reportError(err, extra) {
    try {
      if (window.Sentry && typeof window.Sentry.captureException === 'function') {
        window.Sentry.captureException(err, extra ? { extra: extra } : undefined);
      }
    } catch (e) {}
  }

  // Télémétrie NON-bloquante (niveau info) : ni page d'erreur, ni alerte high-priority. Sert à
  // garder la visibilité sur le volume des POST auxiliaires BTA laissés en passthrough (sinon on
  // deviendrait aveugle en supprimant les faux positifs -4/-5/-7).
  function reportInfo(msg, extra) {
    try {
      if (window.Sentry && typeof window.Sentry.captureMessage === 'function') {
        window.Sentry.captureMessage(msg, { level: 'info', extra: extra });
      }
    } catch (e) {}
  }

  // Comme reportError, mais attend l'envoi réseau de l'event (Sentry.flush) avant d'exécuter
  // `then` — évite la régression du 2026-07-09 où window.top.location.href coupait la requête
  // Sentry avant son départ (event perdu malgré captureException() appelé). Timeout de sécurité
  // 1.5s : si Sentry est absent, bloqué (ad-blocker) ou flush() ne résout jamais, on continue
  // quand même (jamais bloquer la redirection utilisateur pour de la télémétrie).
  function reportErrorThenRedirect(err, extra, then) {
    var done = false;
    var proceed = function () {
      if (done) return;
      done = true;
      then();
    };
    try {
      if (window.Sentry && typeof window.Sentry.captureException === 'function') {
        window.Sentry.captureException(err, extra ? { extra: extra } : undefined);
      }
    } catch (e) {}
    // Sentry absent (ad-blocker, script CDN bloqué) : captureException() n'a rien mis en file,
    // aucun event ne partira jamais — inutile d'imposer le délai ci-dessous. On redirige tout de
    // suite plutôt que d'infliger 900ms de temps mort sur le chemin d'erreur (review #1).
    if (!window.Sentry || typeof window.Sentry.captureException !== 'function') return proceed();
    // Constat prod 2026-07-09 : le script Sentry chargé ici est le "Loader Script" (CDN),
    // un proxy léger (onLoad/init/captureException/...) qui NE PEUT PAS exposer flush() —
    // il met les appels en file d'attente jusqu'au téléchargement asynchrone du vrai SDK.
    // window.Sentry.flush reste `undefined` même plusieurs secondes après captureException().
    // La 1re version de ce fix (2026-07-09, tag flush) était donc un no-op silencieux :
    // sans flush ni délai, elle retombait directement sur proceed() → même course qu'avant.
    // Fix réel : si flush() existe un jour (SDK complet déjà chargé au moment de l'erreur —
    // possible si le forceLoad() du chargement du script, ~l.77 ci-DESSUS, a eu le temps
    // d'aboutir), on l'utilise. Sinon on attend un délai fixe généreux (900ms) avant de naviguer,
    // pour laisser le temps à la requête réseau (queue du loader → SDK complet → envoi) de partir.
    // Pas de garantie absolue (pas de sendBeacon/keepalive côté SDK loader), mais très supérieur
    // à 0ms. Piste suivante si des events manquent encore en prod (review #4) : POST direct
    // fetch(<endpoint Sentry envelope>, { keepalive: true }) sur ce seul chemin d'erreur.
    try {
      if (window.Sentry && typeof window.Sentry.flush === 'function') {
        window.Sentry.flush(1200).then(proceed, proceed);
        setTimeout(proceed, 1500);
        return;
      }
    } catch (e) {}
    setTimeout(proceed, 900);
  }

  // ── Page d'erreur dédiée (respecte la locale via Shopify.routes.root) ─────────
  function goToErrorPage() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    if (root.charAt(root.length - 1) !== '/') root += '/';
    window.top.location.href = root + 'pages/reservation-erreur';
  }

  // Identifiant de corrélation front↔back : transmis en header X-Request-ID, retrouvable
  // dans les logs backend ([checkout/create] booking=... rid=<id>) pour recouper un incident.
  function makeRequestId() {
    try { if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID(); } catch (e) {}
    return 'rid-' + Date.now() + '-' + Math.random().toString(36).slice(2);
  }

  // Démarre le checkout Stripe. Succès → redirection Stripe. Échec → onFail (page d'erreur,
  // après flush Sentry — cf. reportErrorThenRedirect).
  function startStripe(data, onFail) {
    var rid = makeRequestId();
    fetch(BACKEND + '/api/checkout/create', {
      method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Request-ID': rid },
      body: JSON.stringify({
        productId: PRODUCT_ID, variantId: data.variantId, start: data.start, finish: data.finish,
        boatTitle: BOAT_TITLE, locale: LOCALE, cancelUrl: window.location.href,
      }),
    })
      .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, j: j }; }, function () { return { ok: r.ok, j: null }; }); })
      .then(function (res) {
        if (res.ok && res.j && res.j.url) { window.top.location.href = res.j.url; }
        else {
          console.error('[stripe-checkout] rid=' + rid + ' échec:', res.j);
          reportErrorThenRedirect(
            new Error('[stripe-checkout] réponse backend en échec'),
            { rid: rid, ok: res.ok, error: res.j && res.j.error, bookingId: res.j && res.j.bookingId, start: data.start, finish: data.finish, variantId: data.variantId, boatTitle: BOAT_TITLE },
            function () { if (onFail) onFail(); }
          );
        }
      })
      .catch(function (err) {
        console.error('[stripe-checkout] rid=' + rid + ' échec:', err);
        reportErrorThenRedirect(err, { rid: rid }, function () { if (onFail) onFail(); });
      });
  }

  // Masque l'alerte rouge native de BTA pendant qu'on prend la main (sinon flash visuel).
  function suppressBtaAlert(win) {
    try {
      var d = win.document, until = Date.now() + 4000;
      var hide = function () { var n = d.querySelectorAll('.alert.red'); for (var i = 0; i < n.length; i++) n[i].style.display = 'none'; };
      hide(); var iv = setInterval(function () { hide(); if (Date.now() > until) clearInterval(iv); }, 100);
    } catch (e) {}
  }

  // Désigne le widget source ("search" vs "reservation" vs "unknown") à partir de l'id du
  // conteneur wrapper — permet de savoir quel widget a émis un POST /reservations illisible.
  function hintFromContainerId(id) {
    id = String(id || '').toLowerCase();
    if (id.indexOf('reservation') !== -1) return 'reservation';
    if (id.indexOf('search') !== -1) return 'search';
    return 'unknown';
  }

  // ── Arm : wrap le fetch de l'iframe BTA et détourne /reservations vers Stripe ──
  function arm() {
    function wrap(win, hint) {
      if (!win || win.__omybStripeCheckout) return;
      win.__omybStripeCheckout = true;
      win.__omybWidgetHint = hint || 'unknown';
      if (typeof win.fetch !== 'function') return;
      var orig = win.fetch;
      win.fetch = function (input, init) {
        var url = (typeof input === 'string') ? input : (input && input.url) || '';
        var path = url.split('?')[0];
        var method = (((init && init.method) || (input && input.method) || 'GET') + '').toUpperCase();
        var body = (init && init.body) || (input && input.body) || null;
        if (method === 'POST' && RESERVATIONS_PATH_RE.test(path)) {
          if (!body) {
            // POST sans body sur /reservations est anormal ET échapperait au Stripe-only.
            reportError(new Error('[stripe-checkout] POST /reservations sans body'), { url: path, widgetHint: win.__omybWidgetHint });
            return orig.apply(this, arguments);
          }
          // SOUMISSION de réservation (le bouton). On ne la laisse JAMAIS à BTA :
          // Stripe, ou page d'erreur. Aucun fallback Shopify Payments.
          suppressBtaAlert(win);
          var data = parseReservation(body);
          if (data && data.variantId && data.start && data.finish) {
            // Promesse pendante : BTA reste en "envoi" jusqu'à la redirection (succès Stripe
            // ou page d'erreur). On ne rejette/ne redirige que sur échec.
            return new Promise(function (resolve, reject) {
              startStripe(data, function () {
                goToErrorPage();
                reject(new Error('[OmyBoat] checkout Stripe indisponible'));
              });
            });
          }
          // POST sur l'endpoint partagé mais SANS items[0][externalId] : appel auxiliaire de BTA
          // (retry / check_waitlist / check_expires), pas une réservation. On le laisse passer vers
          // BTA au lieu d'éjecter l'utilisateur sur la page d'erreur — cf. Sentry -4/-5/-7 (faux
          // positifs). Télémétrie info uniquement, aucune navigation, aucune alerte error.
          if (isNonReservationPost(body)) {
            reportInfo(
              '[stripe-checkout] POST /reservations non-reservation -> passthrough BTA',
              buildDiag(body, path, win.__omybWidgetHint)
            );
            return orig.apply(this, arguments);
          }
          // Ressemble à une soumission (items présents) mais illisible → on bloque et on envoie sur
          // la page d'erreur, avec le diagnostic scrub-proof en extras Sentry — après flush (2026-07-09).
          return new Promise(function () {
            // pendante : la page navigue vers l'erreur une fois l'event Sentry envoyé (ou timeout)
            reportErrorThenRedirect(
              new Error('[stripe-checkout] données de réservation illisibles (parseReservation)'),
              buildDiag(body, path, win.__omybWidgetHint),
              goToErrorPage
            );
          });
        }
        return orig.apply(this, arguments);
      };
    }
    function findIframe() {
      var w = document.querySelectorAll('[id*="bta"][id*="xcomponent"]');
      for (var i = 0; i < w.length; i++) {
        var f = w[i].querySelector('iframe'); if (!f) continue;
        var win; try { win = f.contentWindow; } catch (e) { continue; }
        if (win && win.document && win.document.body) return { win: win, hint: hintFromContainerId(w[i].id) };
      }
      return null;
    }
    var n = 0, armed = false, poll = setInterval(function () {
      if (++n > 600) {
        clearInterval(poll);
        // Widget BTA jamais apparu en 60s sur une fiche réservable ET onglet visible :
        // personne ne peut réserver ce bateau → signal à remonter (sinon invisible).
        if (!armed && document.visibilityState === 'visible') {
          reportError(new Error('[stripe-checkout] widget BTA introuvable après 60s'));
        }
        return;
      }
      var found = findIframe(); if (found) { wrap(found.win, found.hint); armed = true; }
    }, 100);
  }

  // Plus de gate : on arme TOUJOURS (Stripe-only, sans flag).
  arm();
})();
